// src/components/ui/modal/ShareModal.tsx
'use client';

import React from 'react';
import IconModal from '@/components/ui/modal/IconModal';
import {
  Root as ModalRoot,
  Dialog as ModalDialog,
  Content as ModalContent,
  Title as ModalTitle,
} from './Modal';
import {
  IconCopyLinkFill,
  IconEmailColor,
  IconFacebookColor,
  IconLineColor,
  IconMoreCircleOutline,
} from '@/components/icons';

export type ShareModalProps = {
  /** 是否開啟 */
  open: boolean;
  /** 關閉事件 */
  onClose: () => void;

  /** Modal 標題（預設：分享貼文） */
  title?: string;
  /** Modal 裡面上方的描述文字（可選） */
  description?: React.ReactNode;

  /** 要分享的網址（可傳相對或絕對；不傳會用 window.location.href） */
  url?: string;
  /** 分享標題（Line / Email / Web Share 用） */
  shareTitle?: string;
  /** 分享文字（Email / Web Share 用，通常是 meta description 同一份） */
  shareDescription?: string;

  /** 顯示大小（沿用 Modal.Dialog 的 size） */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** 是否點擊幕後即可關閉（預設 true） */
  closeOnBackdrop?: boolean;
  /** 是否鎖捲動（預設 true） */
  lockScroll?: boolean;
  /** 自訂 Dialog 額外 class */
  dialogClassName?: string;
  /** 自訂 Content 額外 class */
  contentClassName?: string;
};

function getAbsoluteUrl(url?: string): string {
  if (!url) {
    if (typeof window !== 'undefined') return window.location.href;
    return '';
  }
  // 已經是 http 開頭就直接用
  if (/^https?:\/\//i.test(url)) return url;
  // 相對路徑 → 用目前 origin 組絕對路徑
  if (typeof window !== 'undefined') {
    return new URL(url, window.location.origin).toString();
  }
  return url;
}

const ShareModal: React.FC<ShareModalProps> = ({
  open,
  onClose,
  title = '分享',
  description,
  url,
  shareTitle,
  shareDescription,
  size = 'md',
  closeOnBackdrop = true,
  lockScroll = true,
  dialogClassName,
  contentClassName,
}) => {
  const [modalType, setModalType] = React.useState<'success' | null>(null);

  const absoluteUrl = getAbsoluteUrl(url);

  const finalShareTitle = shareTitle || title || 'WOMO 分享';
  const finalShareDesc = shareDescription || (typeof description === 'string' ? description : '');

  // 1) FB 分享
  const handleShareFacebook = () => {
    if (typeof window === 'undefined') return;
    if (!absoluteUrl) return;

    // 組 sharer.php URL
    const shareUrl = new URL('https://www.facebook.com/sharer/sharer.php');

    // 要分享的連結（一定要是「對 FB 爬蟲可存取」的完整網址）
    shareUrl.searchParams.set('u', absoluteUrl);

    // 想顯示在「引用」區塊的文字（不是發文框裡的文字）
    const quote = finalShareDesc || finalShareTitle;
    if (quote) {
      shareUrl.searchParams.set('quote', quote);
    }

    window.open(shareUrl.toString(), '_blank', 'noopener,noreferrer');
  };

  // 2) LINE 分享
  const handleShareLine = () => {
    if (typeof window === 'undefined') return;
    if (!absoluteUrl) return;
    const text = `${finalShareTitle}\n${absoluteUrl}`;
    const lineUrl = `https://line.me/R/msg/text/?${encodeURIComponent(text)}`;
    window.open(lineUrl, '_blank', 'noopener,noreferrer');
  };

  // 3) Email 分享
  const handleShareEmail = () => {
    if (typeof window === 'undefined') return;
    if (!absoluteUrl) return;
    const subject = finalShareTitle || 'WOMO 文章分享';
    const bodyParts = [finalShareDesc, absoluteUrl].filter(Boolean).join('\n\n');
    const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
      bodyParts
    )}`;
    window.location.href = mailto;
  };

  // 4) 複製連結 → 成功後改用 IconModal 提示
  const handleCopyLink = async () => {
    if (typeof window === 'undefined') return;
    if (!absoluteUrl) return;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(absoluteUrl);
        setModalType('success');
        onClose();
      } else {
        // 不支援 Clipboard API，就給個 fallback 提示
        alert('目前瀏覽器不支援自動複製，請手動複製網址');
      }
    } catch (err) {
      console.warn('[ShareModal] copy link failed:', err);
      alert('複製失敗，請稍後再試');
    }
  };

  // 5) 更多：Web Share API
  const handleShareMore = async () => {
    if (typeof window === 'undefined') return;
    if (!absoluteUrl) return;

    try {
      if (navigator.share) {
        await navigator.share({
          title: finalShareTitle,
          text: finalShareDesc || undefined,
          url: absoluteUrl,
        });
      } else {
        await handleCopyLink();
      }
    } catch {
      // 使用者取消 share，不用特別處理
    }
  };

  return (
    <>
      {/* 主分享 Modal */}
      <ModalRoot
        open={open}
        onClose={onClose}
        closeOnBackdrop={closeOnBackdrop}
        lockScroll={lockScroll}
        className="modal-static"
      >
        <ModalDialog size={size} centered className={dialogClassName}>
          <ModalContent className={contentClassName}>
            {/* Header */}
            <div className="modal-header">
              <ModalTitle className="fw-bold">{title}</ModalTitle>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
            </div>

            {/* Body */}
            <div className="modal-body">
              {description && <p className="text-center">{description}</p>}

              <div className="my-3">
                {/* FB */}
                <button
                  onClick={handleShareFacebook}
                  className="btn rounded-pill w-100 bg-grey-50 mb-3"
                >
                  <div className="d-flex align-items-center">
                    <span className="icon">
                      <IconFacebookColor />
                    </span>
                    <span className="ms-2">分享至Facebook</span>
                  </div>
                </button>

                {/* LINE */}
                <button
                  onClick={handleShareLine}
                  className="btn rounded-pill w-100 bg-grey-50 mb-3"
                >
                  <div className="d-flex align-items-center">
                    <span className="icon">
                      <IconLineColor />
                    </span>
                    <span className="ms-2">分享至Line</span>
                  </div>
                </button>

                {/* Email */}
                <button
                  onClick={handleShareEmail}
                  className="btn rounded-pill w-100 bg-grey-50 mb-3"
                >
                  <div className="d-flex align-items-center">
                    <span className="icon">
                      <IconEmailColor />
                    </span>
                    <span className="ms-2">透過電子郵件分享</span>
                  </div>
                </button>

                {/* 複製連結 */}
                <button onClick={handleCopyLink} className="btn rounded-pill w-100 bg-grey-50 mb-3">
                  <div className="d-flex align-items-center">
                    <span className="icon">
                      <IconCopyLinkFill />
                    </span>
                    <span className="ms-2">複製連結</span>
                  </div>
                </button>

                {/* 更多（Web Share） */}
                <button onClick={handleShareMore} className="btn rounded-pill w-100 bg-grey-50">
                  <div className="d-flex align-items-center">
                    <span className="icon">
                      <IconMoreCircleOutline />
                    </span>
                    <span className="ms-2">更多分享方式</span>
                  </div>
                </button>
              </div>
            </div>
          </ModalContent>
        </ModalDialog>
      </ModalRoot>

      {/* 複製成功的提示 IconModal */}
      <IconModal
        show={modalType === 'success'}
        variant="success"
        size="md"
        title="複製連結成功！"
        autoCloseSeconds={2}
        onClose={() => setModalType(null)}
      />
    </>
  );
};

export default ShareModal;
